// communication-chat.component.ts
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { formatDate } from "@angular/common";
import { Router } from "@angular/router";
import { forkJoin, Subscription } from "rxjs";
import { CommunicationService } from "../../services/communication.service";
import { CommunicationContent, Message, MessageDocument, MessageGroup, MessagesResponse, NewMessage } from "../../models/communications.model";
import { NzMessageService } from "ng-zorro-antd/message";

@Component({
    selector: 'app-communication-chat',
    templateUrl: './communication-chat.component.html',
    styleUrl: './communication-chat.component.scss',
    standalone: false
})
export class CommunicationChatComponent implements OnInit, OnDestroy {

  @Input() isFullScreen!: boolean;
  // @ts-ignore
  selectedRequestChat: MessagesResponse;
  message: NewMessage = new NewMessage();
  // @ts-ignore
  messages: Message[];
  // @ts-ignore
  groupedMessages: MessageGroup[];

  // @ts-ignore
  myUserId: number;
  subs: Subscription[] = [];

  isUploading = false;
  attachmentCount: number = 0;

  acceptTypes = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png';
  @ViewChild('chatFileInput') chatFileInput!: ElementRef<HTMLInputElement>;

  @ViewChild('chatBody') chatBody!: ElementRef<HTMLDivElement>;
  @ViewChild('bottomAnchor') bottomAnchor!: ElementRef<HTMLDivElement>;

  constructor(private communicationSrsService: CommunicationService,
              private router: Router, private messageService: NzMessageService) {}

  ngOnInit(): void {
    this.myUserId = this.communicationSrsService.userId;
    this.subs.push(
      this.communicationSrsService.selectedRequestChat$.subscribe(value => {
        if (value) {
          this.communicationSrsService.getMessagesByChat(value.frontOfficeApplicationId).subscribe({
            next: res => {
              this.selectedRequestChat = res;
              this.messages = res.messages;

              this.groupedMessages = this.groupMessagesByDay(this.messages);
              this.scrollToBottom(true);
            },
            error: err => console.error(err)
          })
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  isShowSystemMessage(): boolean {
    return this.selectedRequestChat.isActive == false && this.selectedRequestChat.disableReason != null;
  }

  // external -> true -> слева
  isUserMessage(message: Message): boolean {
    return message.isExternalMessage;
  }

  // internal -> false -> справа
  isOperatorMessage(message: Message): boolean {
    return !message.isExternalMessage;
  }

  onBack() {
    this.communicationSrsService.communicationContent = CommunicationContent.MAIN_REQUESTS;
  }

  onCancel() {
    this.router.navigate([`applications/registration/edit/${this.selectedRequestChat.backOfficeApplicationId}`], { replaceUrl: true });
  }

  sendMessage() {
    if (this.message.content == null || this.message.content.trim() === '') {
      return;
    }
    if (this.message.content != null && this.message.content.length >= 200) {
      this.messageService.error('The message exceeds the 200 character limit.');
      return;
    }
    this.message.applicationId = this.selectedRequestChat.frontOfficeApplicationId;
    this.communicationSrsService.sendMessage(this.message).subscribe(resp => {
      this.message.content = null;
      this.message.documentIds = [];
      this.putMessageToChat(resp);
      this.attachmentCount = 0;
    })
  }

  putMessageToChat(messageResponse: MessagesResponse) {
    this.selectedRequestChat = messageResponse;
    this.messages = messageResponse.messages;

    this.groupedMessages = this.groupMessagesByDay(this.messages);
    this.scrollToBottom(true);
  }

  isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }

  getIconByFileType(type: any) {
    switch (type) {
      case 'application/pdf':
        return 'file-pdf';
      case 'image/jpeg':
      case 'image/jpg':
      case 'image/png':
        return 'picture';
      default:
        return 'file-unknown'
    }
  }

  groupMessagesByDay(messages: Message[]): MessageGroup[] {
    const map = new Map<string, { date: Date; list: Message[] }>();

    for (const msg of messages) {
      const key = this.dayKey(msg.createdAt);
      if (!map.has(key)) {
        map.set(key, { date: this.startOfDay(msg.createdAt), list: [] });
      }
      map.get(key)!.list.push(msg);
    }

    const groups: MessageGroup[] = Array.from(map.values())
      .map(g => ({
        date: g.date,
        messages: g.list.sort(
          (a, b) => this.toDate(a.createdAt).getTime() - this.toDate(b.createdAt).getTime()
        )
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return groups;
  }

  toDate(d: string | Date): Date {
    return d instanceof Date ? d : new Date(d);
  }

  startOfDay(d: string | Date): Date {
    const x = this.toDate(d);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate());
  }

  dayKey(d: string | Date): string {
    return formatDate(d, 'dd.MM.yyy', 'en-US');
  }

  validateFile(file: File): boolean {
    const allowed = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png'
    ];
    if (!allowed.includes(file.type)) {
      console.error('Недопустимый тип файла:', file.type);
      return false;
    }
    if (file.size / 1024 / 1024 > 5) {
      console.error('Файл слишком большой:', file.name);
      return false;
    }
    return true;
  }

  onAttachClick(): void {
    if (!this.selectedRequestChat?.isActive || this.isUploading) return;
    this.chatFileInput?.nativeElement.click();
  }

  onChatFileChange(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);

    const validFiles = files.filter(file => this.validateFile(file));

    if (validFiles.length === 0) {
      input.value = '';
      return;
    }

    this.isUploading = true;

    const uploadObservables = validFiles.map(file =>
      this.communicationSrsService.uploadDocument(file)
    );

    forkJoin(uploadObservables).subscribe({
      next: (docs) => {
        const newIds = docs.map(d => d.id);

        this.message.documentIds = [
          ...(this.message.documentIds || []),
          ...newIds
        ];

        this.attachmentCount = this.message.documentIds.length;
      },
      error: (err) => {
        console.error('Ошибка загрузки файлов:', err);
      },
      complete: () => {
        this.isUploading = false;
        input.value = '';
      }
    });
  }

  isShowSendMessageBar(): boolean {
    return this.selectedRequestChat.isActive;
  }

  isShowNewDivider(last: boolean, lastGroup: boolean, message: Message): boolean {
    return last && lastGroup && message.isExternalMessage;
  }

  private scrollToBottom(smooth = false): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = this.chatBody?.nativeElement;
        if (!el) return;

        if (smooth) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      });
    });
  }

  downloadDocument(doc: MessageDocument): void {
    this.communicationSrsService.downloadDocument(doc.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${doc.fileName}`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => console.error('Ошибка загрузки документа:', err)
    });
  }

  getInitials(chat: Message): string {
    const f = (chat.senderFirstName || '').trim();
    const l = (chat.senderLastName || '').trim();

    const first = f ? f[0] : '';
    const last = l ? l[0] : '';

    const res = (first + last).toUpperCase();
    return res || 'U';
  }
}